import React, { useState } from 'react';
import TeamLogo from './TeamLogo';

function isVet(player, year) {
  if (!player.birthDate) return false;
  return new Date(player.birthDate).getFullYear() <= year - 40;
}

const VetBadge = () => (
  <span style={{ marginLeft: 5, fontSize: '0.62rem', fontWeight: 700, color: '#fff', background: '#0e244a', borderRadius: 3, padding: '1px 5px', verticalAlign: 'middle' }}>VET</span>
);

const SuspBadge = () => (
  <span style={{ marginLeft: 5, fontSize: '0.62rem', fontWeight: 700, color: '#fff', background: '#a73022', borderRadius: 3, padding: '1px 5px', verticalAlign: 'middle' }}>SUSPENSO</span>
);

const PendBadge = () => (
  <span style={{ marginLeft: 5, fontSize: '0.62rem', fontWeight: 700, color: '#0e244a', background: '#c89525', border: '1px solid #a07415', borderRadius: 3, padding: '1px 6px', verticalAlign: 'middle' }}>pendurado</span>
);

const POSITION_SHORT = {
  GOALKEEPER: 'GOL',
  DEFENDER:   'DEF',
  MIDFIELDER: 'MEI',
  FORWARD:    'ATA',
};

function normalize(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export default function TournamentPlayers({ players, tournamentYear = new Date().getFullYear(), onTeamClick }) {
  const [query, setQuery] = useState('');
  const [scorerLimit, setScorerLimit] = useState('top10');
  const [disciplinaryLimit, setDisciplinaryLimit] = useState('top10');

  if (!players || players.length === 0) {
    return (
      <div className="camp-empty">
        <span className="camp-empty-icon">⚽</span>
        <p>Artilharia ainda não disponível.</p>
      </div>
    );
  }

  const scorers      = [...players].sort((a, b) => b.goals - a.goals || b.yellowCards - a.yellowCards || a.name.localeCompare(b.name, 'pt-BR'));
  const disciplinary = [...players].filter(p => p.yellowCards > 0 || p.redCards > 0)
    .sort((a, b) => b.redCards - a.redCards || b.yellowCards - a.yellowCards);

  // rank real de cada jogador na lista completa
  const rankMap = new Map(scorers.map((p, i) => [p.id, i + 1]));

  const trimmed = query.trim();
  const filteredScorers = trimmed
    ? scorers.filter(p => normalize(p.name).includes(normalize(trimmed)))
    : scorers;
  const visibleScorers = scorerLimit === 'top10'
    ? filteredScorers.slice(0, 10)
    : filteredScorers;
  const visibleDisciplinary = disciplinaryLimit === 'top10'
    ? disciplinary.slice(0, 10)
    : disciplinary;
  
  // Top 3 para destaque
  const topThree = scorers.slice(0, 3);

  return (
    <div className="camp-players">

      {/* ── Top 3 Artilheiros ── */}
      {topThree.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 className="camp-group-title">⚽ Artilheiros em Destaque</h3>
          <div className="cp-top-scorers">
            {topThree.map((p, idx) => (
              <div key={p.id} className={`cp-scorer-card cp-scorer-rank-${idx + 1}`}>
                <div className="cp-scorer-medal">
                  <span className="cp-scorer-pos">#{idx + 1}</span>
                </div>
                <TeamLogo name={p.team?.name || '?'} logoUrl={p.team?.logoUrl || null} size={36} shape="shield" />
                <div className="cp-scorer-info">
                  <span className="cp-scorer-name">{p.name}</span>
                  <span className="cp-scorer-team">{p.team?.name ?? '—'}</span>
                </div>
                <div className="cp-scorer-stats">
                  <div className="cp-scorer-stat">
                    <span className="cp-scorer-stat-value">{p.goals}</span>
                    <span className="cp-scorer-stat-label">Gols</span>
                  </div>
                  {p.yellowCards > 0 && (
                    <div className="cp-scorer-stat">
                      <span className="cp-scorer-stat-value" style={{ color: '#d68910' }}>{p.yellowCards}</span>
                      <span className="cp-scorer-stat-label">AM</span>
                    </div>
                  )}
                  {p.redCards > 0 && (
                    <div className="cp-scorer-stat">
                      <span className="cp-scorer-stat-value" style={{ color: '#c0392b' }}>{p.redCards}</span>
                      <span className="cp-scorer-stat-label">VM</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Artilharia Completa ── */}
      <div className="camp-group-section">
        <div className="camp-section-head">
          <h3 className="camp-group-title">Artilharia Completa</h3>
          <div className="camp-segmented" aria-label="Filtro da artilharia">
            <button type="button" className={scorerLimit === 'top10' ? 'active' : ''} onClick={() => setScorerLimit('top10')}>
              Top 10
            </button>
            <button type="button" className={scorerLimit === 'all' ? 'active' : ''} onClick={() => setScorerLimit('all')}>
              Todos
            </button>
          </div>
        </div>

        <div className="camp-search-row">
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar jogador pelo nome..."
          />
        </div>

        <div className="camp-table-wrap">
          <table className="camp-table">
            <thead>
              <tr>
                <th className="camp-th pos">#</th>
                <th className="camp-th">Jogador</th>
                <th className="camp-th team" style={{ minWidth: 160 }}>Time</th>
                <th className="camp-th center">Pos.</th>
                <th className="camp-th center pts">Gols</th>
                <th className="camp-th center" title="Cartões Amarelos"><svg viewBox="0 0 10 14" width="10" height="14"><rect x="1" y="1" width="8" height="12" rx="1.5" fill="#eab308"/></svg></th>
                <th className="camp-th center" title="Cartões Vermelhos"><svg viewBox="0 0 10 14" width="10" height="14"><rect x="1" y="1" width="8" height="12" rx="1.5" fill="#dc2626"/></svg></th>
              </tr>
            </thead>
            <tbody>
              {visibleScorers.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '16px 0', color: '#888', fontSize: '0.875rem' }}>
                    Nenhum jogador encontrado.
                  </td>
                </tr>
              ) : (
                visibleScorers.map(p => (
                  <tr key={p.id} className="camp-tr">
                    <td className="camp-td pos">{rankMap.get(p.id)}</td>
                    <td className="camp-td" style={{ fontWeight: 600 }}>
                      {p.name}
                      {isVet(p, tournamentYear) && <VetBadge />}
                      {p.suspended && <SuspBadge />}
                      {!p.suspended && p.yellowCardAccum === 2 && <PendBadge />}
                    </td>
                    <td className="camp-td">
                      <span
                        className="ts-team-cell"
                        onClick={onTeamClick && p.team ? () => onTeamClick({ name: p.team.name, logoUrl: p.team.logoUrl }) : undefined}
                        style={onTeamClick && p.team ? { cursor: 'pointer' } : undefined}
                      >
                        {p.team && (
                          <TeamLogo name={p.team.name} logoUrl={p.team.logoUrl} size={24} shape="shield" />
                        )}
                        <span className={onTeamClick && p.team ? 'camp-team-clickable' : ''} style={{ fontSize: '0.875rem' }}>{p.team?.name ?? '—'}</span>
                      </span>
                    </td>
                    <td className="camp-td center">
                      {p.position ? (
                        <span style={{
                          fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px',
                          borderRadius: 4, background: '#e9ecef', color: '#495057',
                        }}>
                          {POSITION_SHORT[p.position] || p.position}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="camp-td center pts" style={{ fontWeight: 800 }}>{p.goals}</td>
                    <td className="camp-td center"
                      style={{ color: p.yellowCards > 0 ? '#d68910' : undefined }}>
                      {p.yellowCards || '—'}
                    </td>
                    <td className="camp-td center"
                      style={{ color: p.redCards > 0 ? '#c0392b' : undefined }}>
                      {p.redCards || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Ranking Disciplinar ── */}
      {disciplinary.length > 0 && (
        <div className="camp-group-section" style={{ marginTop: 32 }}>
          <div className="camp-section-head">
            <h3 className="camp-group-title">Ranking Disciplinar</h3>
            <div className="camp-segmented" aria-label="Filtro do ranking disciplinar">
              <button type="button" className={disciplinaryLimit === 'top10' ? 'active' : ''} onClick={() => setDisciplinaryLimit('top10')}>
                Top 10
              </button>
              <button type="button" className={disciplinaryLimit === 'all' ? 'active' : ''} onClick={() => setDisciplinaryLimit('all')}>
                Todos
              </button>
            </div>
          </div>
          <div className="camp-table-wrap">
            <table className="camp-table">
              <thead>
                <tr>
                  <th className="camp-th">Jogador</th>
                  <th className="camp-th team" style={{ minWidth: 160 }}>Time</th>
                  <th className="camp-th center"><svg viewBox="0 0 10 14" width="10" height="14" style={{marginRight:4,verticalAlign:'middle'}}><rect x="1" y="1" width="8" height="12" rx="1.5" fill="#eab308"/></svg>Amarelos</th>
                  <th className="camp-th center"><svg viewBox="0 0 10 14" width="10" height="14" style={{marginRight:4,verticalAlign:'middle'}}><rect x="1" y="1" width="8" height="12" rx="1.5" fill="#dc2626"/></svg>Vermelhos</th>
                </tr>
              </thead>
              <tbody>
                {visibleDisciplinary.map(p => (
                  <tr key={p.id} className="camp-tr">
                    <td className="camp-td" style={{ fontWeight: 600 }}>
                      {p.name}
                      {isVet(p, tournamentYear) && <VetBadge />}
                      {p.suspended && <SuspBadge />}
                      {!p.suspended && p.yellowCardAccum === 2 && <PendBadge />}
                    </td>
                    <td className="camp-td">
                      <span className="ts-team-cell">
                        {p.team && (
                          <TeamLogo name={p.team.name} logoUrl={p.team.logoUrl} size={24} shape="shield" />
                        )}
                        <span style={{ fontSize: '0.875rem' }}>{p.team?.name ?? '—'}</span>
                      </span>
                    </td>
                    <td className="camp-td center">
                      {p.yellowCards > 0
                        ? <span style={{ color: '#d68910', fontWeight: 700 }}>{p.yellowCards}</span>
                        : '—'
                      }
                    </td>
                    <td className="camp-td center">
                      {p.redCards > 0
                        ? <span style={{ color: '#c0392b', fontWeight: 700 }}>{p.redCards}</span>
                        : '—'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="camp-legend" style={{ marginTop: 8 }}>
            Critério de desempate na tabela: menos cartões vermelhos, depois menos amarelos.
          </p>
        </div>
      )}
    </div>
  );
}
